"use client";

import { useShallow } from "zustand/react/shallow";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { CreateFormData } from "@/components/create/types";
import { useAuth } from "@/context/providers/AuthProvider";
import { ensureAnonymousSession } from "@/lib/auth/anonymousSession";
import { hasVerifiedAccount } from "@/lib/auth/accountStatus";
import { cn } from "@/lib/utils";
import { projectDesignDetails } from "@/lib/designer/designDetailRows";
import { markAgentSessionReset } from "@/hooks/create-page/utils/agentSessionId";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { hasRenderableChatTranscript } from "@/lib/agent/renderableChatMessage";
import {
  listenForVirtualTryOnRequests,
  requestVirtualTryOn,
} from "@/lib/create/virtualTryOnEvents";
import { useCreatePageStore } from "@/stores/createPageStore";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CreateFullSizeImageModal from "../CreateFullSizeImageModal";
import CreateAgentChat from "./chat/CreateAgentChat";
import LoginRequiredDialog from "./chat/LoginRequiredDialog";
import CreateWorkspaceAppBar from "./CreateWorkspaceAppBar";
import DesktopDressDesignCanvas, {
  type MobileFrontReviewIntent,
} from "./DesktopDressDesignCanvas";
import {
  DesignerWorkspaceProvider,
  useDesignerWorkspace,
} from "./DesignerWorkspaceProvider";
import VirtualTryOnDialog, {
  type TryOnWorkspaceStatus,
} from "./VirtualTryOnDialog";
import DesignerPanelNavigation, {
  type MobileDesignerView,
} from "./DesignerPanelNavigation";
import { useDesignerPanel } from "./useDesignerPanel";
import { usePreviewNotifications } from "./usePreviewNotifications";
import CreateEntryPoint, {
  type EntryConversationOptions,
} from "./entry/CreateEntryPoint";
import { useChatActions } from "./ChatActionsProvider";
import { useApplyDesignChanges } from "./useApplyDesignChanges";

export const ENTRY_IDENTITY_READY_TIMEOUT_MS = 8_000;

interface CreateAgentLayoutProps {
  restorationPending?: boolean;
  sessionRestoreError?: string | null;
  onRetrySessionRestore?: () => void;
  sessionRecoveryHref?: string | null;
  sessionSignInHref?: string | null;
}

interface PendingEntryConversation {
  id: number;
  message: string;
  createdAt: number;
  imageUrls?: string[];
  formUpdates: Partial<CreateFormData>;
  options: EntryConversationOptions;
  ready: boolean;
}

interface PendingEntryIdentityWait {
  conversationId: number;
  settle: () => void;
}

const IDLE_TRY_ON_STATUS: TryOnWorkspaceStatus = {
  status: "idle",
  error: null,
};

function latestUserMessageId(
  messages: readonly { id: string; role: "user" | "assistant" }[]
): string | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === "user") return messages[index].id;
  }
  return undefined;
}

export default function CreateAgentLayout(props: CreateAgentLayoutProps) {
  return (
    <DesignerWorkspaceProvider
      blockedReason={
        props.restorationPending ? "Restoring your saved design." : null
      }
    >
      <DesignerWorkspaceLayout {...props} />
    </DesignerWorkspaceProvider>
  );
}

function DesignerWorkspaceLayout({
  restorationPending = false,
  sessionRestoreError = null,
  onRetrySessionRestore,
  sessionRecoveryHref = null,
  sessionSignInHref = null,
}: CreateAgentLayoutProps) {
  const { handleStartOver, sendAgentMessage } = useChatActions();
  const { session, loading: authLoading, refreshSession } = useAuth();
  const loading = useCreatePageStore(
    state => state.generation.progress.loading
  );
  const updateFormData = useCreatePageStore(state => state.updateFormData);
  const designSubmitted = useCreatePageStore(
    state => state.ticket.designSubmitted
  );
  const ticketId = useCreatePageStore(state => state.ticket.id);
  const messages = useCreatePageStore(
    useShallow(state => state.agent.conversation.messages)
  );
  const isTyping = useCreatePageStore(
    state => state.agent.conversation.isTyping
  );
  const pendingComposerSeed = useCreatePageStore(
    state => state.agent.pendingComposerSeed
  );
  const pendingComposerRecovery = useCreatePageStore(
    state => state.agent.pendingComposerRecovery
  );
  const entryTransition = useCreatePageStore(
    state => state.agent.entryTransition
  );
  const setEntryTransition = useCreatePageStore(
    state => state.setEntryTransition
  );
  const imageUrl = useCreatePageStore(
    useShallow(state => state.generation.images.front.url)
  );
  const frontImagePath = useCreatePageStore(
    state => state.generation.images.front.path
  );
  const backImageUrl = useCreatePageStore(
    useShallow(state => state.generation.images.back.url)
  );
  const backImagePath = useCreatePageStore(
    state => state.generation.images.back.path
  );
  const designImages = useCreatePageStore(state => state.generation.images);
  const isModifying = useCreatePageStore(
    state => state.ui.modification.isModifying
  );
  const setCurrentView = useCreatePageStore(state => state.setCurrentView);
  const activeView = useCreatePageStore(state => state.ui.view);
  const projectActionEpoch = useCreatePageStore(
    state => state.projectActionEpoch
  );
  const generationError = useCreatePageStore(
    state => state.generation.errors?.message
  );
  const quoteError = useCreatePageStore(state => state.ticket.submissionError);
  const generationPhase = useCreatePageStore(
    state => state.generation.progress.phase
  );
  const agentSessionId = useCreatePageStore(state => state.agent.sessionId);
  const panelIdentityKey = `${session?.user?.id ?? "guest"}:${projectActionEpoch}`;
  const panelStorageKey =
    agentSessionId || ticketId
      ? `${session?.user?.id ?? "guest"}:${agentSessionId ?? ticketId}`
      : null;
  const { panel: mobilePanel, selectPanel } = useDesignerPanel(
    panelStorageKey,
    panelIdentityKey
  );
  const isDesktopWorkspace = useMediaQuery("(min-width: 1024px)");
  const openConversationRef = useRef<
    ((intent?: MobileFrontReviewIntent) => void) | null
  >(null);
  const openHelpRef = useRef<(() => void) | null>(null);
  const selectedMobileViewRef = useRef<MobileDesignerView>("chat");
  const renderStageHandledRef = useRef(false);
  const [mobileAttentionHost, setMobileAttentionHost] =
    useState<HTMLDivElement | null>(null);
  const [mobileComposerHost, setMobileComposerHost] =
    useState<HTMLDivElement | null>(null);
  const virtualTryOnUnavailable =
    !imageUrl?.trim() ||
    !ticketId?.trim() ||
    loading ||
    isModifying ||
    restorationPending;
  const virtualTryOnAccountReady = hasVerifiedAccount(session?.user);
  const virtualTryOnSafetyUnavailable =
    virtualTryOnUnavailable || authLoading || !virtualTryOnAccountReady;

  const workspace = useDesignerWorkspace()!;
  const applyChanges = useApplyDesignChanges();
  const pendingDetailCount = applyChanges.hasPendingChanges
    ? Math.max(
        1,
        projectDesignDetails({
          side: activeView,
          draft: applyChanges.draft ?? null,
          images: designImages,
          changes: applyChanges.changes,
        }).pendingCount
      )
    : 0;
  const desktopRenderActionState = workspace.renderActionState;
  const desktopRenderActionId =
    desktopRenderActionState.capability?.actionId ??
    desktopRenderActionState.unavailableActionId;
  const desktopFooterAuthorityPending = Boolean(
    workspace.renderProjection.unavailableReason
  );

  const [showFullSizeImage, setShowFullSizeImage] = useState(false);
  const [composerAttachmentPending, setComposerAttachmentPending] =
    useState(false);
  // Shared via the store so an agent-offered "Start over" preset opens the same
  // confirmation modal instead of wiping the design without asking.
  const showStartOverConfirm = useCreatePageStore(
    state => state.ui.modals.startOverConfirm
  );
  const setShowStartOverConfirm = useCreatePageStore(
    state => state.setShowStartOverConfirm
  );
  const [initialView, setInitialView] = useState<"front" | "back">("front");
  const resetExampleTypingRef = useRef<(() => void) | null>(null);
  const startOverReturnFocusRef = useRef<HTMLElement | null>(null);
  const desktopDetailsTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileDetailsTriggerRef = useRef<HTMLButtonElement>(null);
  const designDetailsPanelId = React.useId();
  const mobileDesignDetailsPanelId = React.useId();
  const [designDetailsState, setDesignDetailsState] = useState({
    open: false,
    projectActionEpoch,
  });
  const [virtualTryOnState, setVirtualTryOnState] = useState({
    open: false,
    projectActionEpoch,
  });
  const [showVirtualTryOnAccountGate, setShowVirtualTryOnAccountGate] =
    useState(false);
  const [previousVirtualTryOnUnavailable, setPreviousVirtualTryOnUnavailable] =
    useState(virtualTryOnUnavailable);
  const [virtualTryOnPending, setVirtualTryOnPending] = useState(false);
  const tryOnScope = `${projectActionEpoch}:${latestUserMessageId(messages) ?? ""}`;
  const activeTryOnScopeRef = useRef<string | null>(null);
  const [tryOnStatusRecord, setTryOnStatusRecord] = useState({
    scope: tryOnScope,
    value: IDLE_TRY_ON_STATUS,
  });
  const setTryOnStatus = useCallback((value: TryOnWorkspaceStatus) => {
    const current = useCreatePageStore.getState();
    const currentScope = `${current.projectActionEpoch}:${latestUserMessageId(current.agent.conversation.messages) ?? ""}`;
    if (value.status === "generating") {
      // Keep the whole attempt attached to the turn that started it. The
      // request can finish after the customer has sent another chat message.
      activeTryOnScopeRef.current = currentScope;
    }
    if (value.status === "idle") {
      activeTryOnScopeRef.current = null;
      setTryOnStatusRecord({ scope: currentScope, value });
      return;
    }
    const scope = activeTryOnScopeRef.current;
    if (!scope) return;
    setTryOnStatusRecord({
      scope,
      value,
    });
  }, []);
  // A completed attempt belongs to the turn in which it started. A new user
  // turn reveals its own actions even if the old try-on request finishes late.
  const tryOnStatus =
    tryOnStatusRecord.scope === tryOnScope
      ? tryOnStatusRecord.value
      : IDLE_TRY_ON_STATUS;
  const layoutMountedRef = useRef(true);
  const nextEntryConversationIdRef = useRef(0);
  const pendingEntryConversationRef = useRef<PendingEntryConversation | null>(
    null
  );
  const pendingEntryIdentityWaitRef = useRef<PendingEntryIdentityWait | null>(
    null
  );
  const [pendingEntryConversation, setPendingEntryConversation] =
    useState<PendingEntryConversation | null>(null);
  const [failedEntryConversation, setFailedEntryConversation] =
    useState<PendingEntryConversation | null>(null);
  const [entryStartError, setEntryStartError] = useState<string | null>(null);

  if (previousVirtualTryOnUnavailable !== virtualTryOnUnavailable) {
    setPreviousVirtualTryOnUnavailable(virtualTryOnUnavailable);
    if (virtualTryOnUnavailable) {
      if (virtualTryOnState.open) {
        setVirtualTryOnState(current => ({ ...current, open: false }));
      }
      if (showVirtualTryOnAccountGate) {
        setShowVirtualTryOnAccountGate(false);
      }
    }
  }

  useEffect(() => {
    layoutMountedRef.current = true;
    return () => {
      layoutMountedRef.current = false;
      // Release the bounded wait as well as clearing its timer. The abandoned
      // layout must not retain a suspended promise until an auth request that
      // may never settle finally completes.
      const pendingIdentityWait = pendingEntryIdentityWaitRef.current;
      pendingEntryIdentityWaitRef.current = null;
      pendingIdentityWait?.settle();
    };
  }, []);

  const handleImageClick = (view: "front" | "back") => {
    setCurrentView(view);
    setInitialView(view);
    setShowFullSizeImage(true);
  };

  const hasDesignImage = Boolean(imageUrl || backImageUrl);
  // Once explicitly opened, keep the inspector stable while a regeneration
  // temporarily clears the current image. A new project epoch still closes it.
  const designDetailsOpen =
    designDetailsState.open &&
    designDetailsState.projectActionEpoch === projectActionEpoch;
  const setDesignDetailsOpen = useCallback(
    (open: boolean) => {
      setDesignDetailsState({ open, projectActionEpoch });
    },
    [projectActionEpoch]
  );
  const virtualTryOnOpen =
    virtualTryOnState.open &&
    virtualTryOnState.projectActionEpoch === projectActionEpoch &&
    !virtualTryOnSafetyUnavailable;
  const setVirtualTryOnOpen = useCallback(
    (open: boolean) => {
      setVirtualTryOnState({ open, projectActionEpoch });
    },
    [projectActionEpoch]
  );

  useEffect(
    () =>
      listenForVirtualTryOnRequests(() => {
        // Apply-first can finish before this effect's loading closure refreshes.
        // Re-read the generated preview at the same boundary as the request.
        const current = useCreatePageStore.getState();
        if (
          restorationPending ||
          authLoading ||
          !current.generation.images.front.url?.trim() ||
          !current.ticket.id?.trim() ||
          current.generation.progress.loading ||
          current.ui.modification.isModifying
        )
          return;
        if (!virtualTryOnAccountReady) {
          setShowVirtualTryOnAccountGate(true);
          return;
        }
        setShowVirtualTryOnAccountGate(false);
        setVirtualTryOnOpen(true);
      }),
    [
      authLoading,
      setVirtualTryOnOpen,
      virtualTryOnAccountReady,
      restorationPending,
    ]
  );
  const virtualTryOnAccountGateOpen =
    showVirtualTryOnAccountGate &&
    !virtualTryOnUnavailable &&
    !virtualTryOnAccountReady;
  // A credit/auth handoff can leave `entryChatActive` set in the long-lived
  // client store even when there is nothing renderable. Derive ownership from
  // visible content instead: a transcript, restored design, or composer text.
  // This also handles legacy transcripts whose messages are all hidden.
  const hasVisibleMessage = hasRenderableChatTranscript(messages);
  const hasRestoredComposerText = Boolean(
    pendingComposerSeed?.trim() || pendingComposerRecovery
  );
  const showAgentChatShell =
    Boolean(pendingEntryConversation) ||
    hasVisibleMessage ||
    hasDesignImage ||
    hasRestoredComposerText;
  const hasStartedConversation =
    Boolean(pendingEntryConversation) || hasVisibleMessage || hasDesignImage;
  const visibleMobilePanel = hasStartedConversation ? mobilePanel : "chat";
  const mobileView: MobileDesignerView =
    visibleMobilePanel === "chat"
      ? "chat"
      : designDetailsOpen
        ? "details"
        : activeView;
  useEffect(() => {
    selectedMobileViewRef.current = mobileView;
  }, [mobileView]);
  const selectMobileView = (view: MobileDesignerView) => {
    selectedMobileViewRef.current = view;
    if (view === "chat") {
      selectPanel("chat");
      openConversationRef.current?.();
      return;
    }
    selectPanel("design");
    // The shared composer is an edit entry point on every design view, even if
    // the customer last opened the separate Help & Q&A surface in Chat.
    openConversationRef.current?.();
    setDesignDetailsOpen(view === "details");
    if (view !== "details") setCurrentView(view);
  };
  const renderingSide =
    (loading || isModifying) &&
    (generationPhase === "front" || generationPhase === "back")
      ? generationPhase
      : null;
  useEffect(() => {
    if (!loading && !isModifying && !applyChanges.busyStep)
      renderStageHandledRef.current = false;
  }, [applyChanges.busyStep, isModifying, loading]);
  useEffect(
    () =>
      useCreatePageStore.subscribe((next, previous) => {
        const phase = next.generation.progress.phase;
        const rendering =
          next.generation.progress.loading || next.ui.modification.isModifying;
        const wasRendering =
          previous.generation.progress.loading ||
          previous.ui.modification.isModifying;
        if (
          isDesktopWorkspace ||
          !rendering ||
          (phase !== "front" && phase !== "back") ||
          wasRendering ||
          renderStageHandledRef.current
        )
          return;
        // The first front render opens its preview from any mobile view. Later
        // renders started from another view leave the user where they chose to
        // work; the footer carries progress there.
        renderStageHandledRef.current = true;
        const firstFrontRender =
          phase === "front" && !previous.generation.images.front.url?.trim();
        if (selectedMobileViewRef.current !== "chat" && !firstFrontRender)
          return;
        selectedMobileViewRef.current = phase;
        selectPanel("design");
        setDesignDetailsOpen(false);
        setCurrentView(phase);
        requestAnimationFrame(() => {
          if (selectedMobileViewRef.current === phase)
            document
              .getElementById(`designer-mobile-tab-${phase}`)
              ?.focus({ preventScroll: true });
        });
      }),
    [isDesktopWorkspace, selectPanel, setDesignDetailsOpen, setCurrentView]
  );
  const unreadPreviews = usePreviewNotifications({
    storageKey: panelStorageKey,
    storageAliases: ticketId
      ? [`${session?.user?.id ?? "guest"}:${ticketId}`]
      : [],
    identityKey: panelIdentityKey,
    images: designImages,
    visibleSides: isDesktopWorkspace
      ? designDetailsOpen
        ? [activeView]
        : ["front", "back"]
      : mobileView === "front" || mobileView === "back"
        ? [mobileView]
        : [],
    renderingSide,
    restorationPending,
  });
  const workspaceActionDisabled =
    Boolean(pendingEntryConversation) ||
    loading ||
    isTyping ||
    authLoading ||
    composerAttachmentPending ||
    restorationPending ||
    entryTransition === "starting";
  const renderWorkspaceAppBar = (showPageHeading = true) => (
    <CreateWorkspaceAppBar
      showNewDress={showAgentChatShell}
      newDressDisabled={workspaceActionDisabled}
      showPageHeading={showPageHeading}
      onNewDress={() => setShowStartOverConfirm(true)}
      onDesignHelp={
        hasStartedConversation && !isDesktopWorkspace
          ? () => {
              selectPanel("chat");
              openHelpRef.current?.();
            }
          : undefined
      }
    />
  );
  const failedReferenceImages =
    failedEntryConversation?.options.referenceImages?.files.flatMap(
      (file, index) => {
        const referenceImages = failedEntryConversation.options.referenceImages;
        const dataUrl =
          referenceImages?.previewUrls?.[index] ?? referenceImages?.urls[index];
        return dataUrl ? [{ file, dataUrl }] : [];
      }
    ) ?? [];

  const startEntryConversation = useCallback(
    (message: string, options: EntryConversationOptions = {}) => {
      const trimmedMessage = message.trim();
      if (
        !trimmedMessage ||
        loading ||
        authLoading ||
        pendingEntryConversationRef.current
      ) {
        return;
      }

      const entryConversation: PendingEntryConversation = {
        id: ++nextEntryConversationIdRef.current,
        message: trimmedMessage,
        createdAt: Date.now(),
        imageUrls: options.referenceImages?.urls,
        formUpdates: {
          // The inspiration starter is conversational routing, not a dress
          // description. Reference analysis authors the actual design prompt
          // after the customer uploads an image.
          prompt: options.persistAsDesignPrompt === false ? "" : trimmedMessage,
          ...(options.presetType ? { preset_type: options.presetType } : {}),
          ...(options.referenceImages
            ? {
                uploadedFrontImages: options.referenceImages.files,
                // Inline fallbacks are turn-local vision input. Persist only
                // durable hosted URLs; otherwise every later saved brief and
                // planner request would duplicate megabytes of base64.
                uploadedFrontImageUrls: options.referenceImages.urls.filter(
                  url => /^https?:\/\//i.test(url)
                ),
                uploadedFrontImageCount: options.referenceImages.files.length,
                isUploadPreset: true,
                isFrontUploadPreset: true,
              }
            : {}),
        },
        options,
        ready: false,
      };
      setEntryStartError(null);
      selectPanel("chat");
      setFailedEntryConversation(null);
      pendingEntryConversationRef.current = entryConversation;
      setEntryTransition("starting");
      setPendingEntryConversation(entryConversation);

      void (async () => {
        let identityWasCreated = options.identityCreated === true;
        if (!session && !options.identityReady) {
          const identityResult = await ensureAnonymousSession().catch(() => ({
            ok: false,
            created: false,
          }));
          const current = pendingEntryConversationRef.current;
          if (
            !layoutMountedRef.current ||
            !current ||
            current.id !== entryConversation.id
          ) {
            return;
          }

          // Never send the first turn without an owning identity. The agent
          // route can answer a signed-out request, but it cannot persist the
          // session or reliably attach credits. Return to the entry surface so
          // the customer can retry instead of silently creating orphaned work.
          if (!identityResult.ok) {
            pendingEntryConversationRef.current = null;
            setFailedEntryConversation(entryConversation);
            setPendingEntryConversation(null);
            setEntryTransition("idle");
            setEntryStartError(
              "We couldn't start your design session. Check your connection and try again."
            );
            return;
          }
          identityWasCreated = identityResult.created;
        }

        if (!session && identityWasCreated) {
          // A just-minted account cannot have a server-side dress to restore.
          // Suppress the otherwise automatic latest-session fallback during
          // provider hydration; the first send replaces this one-shot marker
          // with its freshly minted conversation id.
          markAgentSessionReset();
        }

        // Cookies from a successful anonymous-session request are usable by
        // the agent route as soon as that request resolves. Give the provider
        // a bounded window to hydrate local ownership/restoration state; a
        // stalled refresh must not discard the now-proven cookie identity.
        let settled = false;
        let timeoutId: number | null = null;
        let resolveIdentityWait!: () => void;
        const identityWait = new Promise<void>(resolve => {
          resolveIdentityWait = resolve;
        });
        const settleIdentityWait = () => {
          if (settled) return;
          settled = true;
          if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
          }
          if (
            pendingEntryIdentityWaitRef.current?.conversationId ===
            entryConversation.id
          ) {
            pendingEntryIdentityWaitRef.current = null;
          }
          resolveIdentityWait();
        };
        pendingEntryIdentityWaitRef.current = {
          conversationId: entryConversation.id,
          settle: settleIdentityWait,
        };
        timeoutId = window.setTimeout(
          settleIdentityWait,
          ENTRY_IDENTITY_READY_TIMEOUT_MS
        );
        void (
          session ? Promise.resolve() : refreshSession().catch(() => {})
        ).then(settleIdentityWait);
        await identityWait;

        // The entry card is expected to unmount while anonymous auth/session
        // restoration runs. The layout remains mounted and owns this exact
        // turn; a real route departure retires the layout in the cleanup above.
        const current = pendingEntryConversationRef.current;
        if (
          !layoutMountedRef.current ||
          !current ||
          current.id !== entryConversation.id
        ) {
          return;
        }
        const readyConversation = { ...current, ready: true };
        pendingEntryConversationRef.current = readyConversation;
        setPendingEntryConversation(readyConversation);
      })();
    },
    [
      authLoading,
      loading,
      refreshSession,
      selectPanel,
      session,
      setEntryTransition,
    ]
  );

  useEffect(() => {
    if (!pendingEntryConversation?.ready || restorationPending || authLoading) {
      return;
    }

    const conversationId = pendingEntryConversation.id;
    if (!showAgentChatShell) {
      setEntryTransition("starting");
    }
    const timer = window.setTimeout(() => {
      const current = pendingEntryConversationRef.current;
      if (
        !layoutMountedRef.current ||
        !current ||
        current.id !== conversationId ||
        !current.ready
      ) {
        return;
      }

      // Clear the ref before dispatch so an effect replay cannot send this
      // exact first turn twice. sendAgentMessage appends the visible customer
      // turn synchronously before its first network await; only then clear the
      // render-state flag that protects the entry transition reset.
      pendingEntryConversationRef.current = null;
      updateFormData(current.formUpdates);
      const request = sendAgentMessage(
        current.message,
        current.imageUrls?.[0],
        current.imageUrls,
        {
          formStateOverride: current.formUpdates,
          recoverComposerOnFailure: true,
          ...(current.options.entryPoint && {
            entryPoint: current.options.entryPoint,
          }),
        }
      );
      setPendingEntryConversation(null);
      // The transport owner preserves the visible turn and seeds the active
      // chat composer on ambiguous failures. Absorb its recovery rejection so
      // the effect never creates an unhandled promise.
      void request.catch(() => {});
    }, 0);

    return () => window.clearTimeout(timer);
  }, [
    authLoading,
    pendingEntryConversation,
    restorationPending,
    sendAgentMessage,
    setEntryTransition,
    showAgentChatShell,
    updateFormData,
  ]);

  // Keep the shared transition bounded even though the outer chat shell stays
  // mounted across the empty-to-first-turn handoff.
  useEffect(() => {
    if (!showAgentChatShell || entryTransition !== "starting") return;
    const timer = window.setTimeout(() => setEntryTransition("settled"), 320);
    return () => window.clearTimeout(timer);
  }, [entryTransition, setEntryTransition, showAgentChatShell]);

  if (restorationPending && !pendingEntryConversation) {
    return (
      <div
        data-slot="create-workspace"
        className="designer-workspace flex min-h-0 w-full flex-1 flex-col overflow-hidden"
      >
        {renderWorkspaceAppBar()}
        <div
          role="status"
          aria-live="polite"
          data-slot="create-restore-pending"
          className="flex min-h-0 w-full flex-1 items-center justify-center p-6"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-sm">
            <LoadingSpinner size="md" />
            <Text as="p" variant="primary" size="sm" weight="semibold">
              Restoring your dress…
            </Text>
          </div>
        </div>
      </div>
    );
  }

  if (sessionRestoreError) {
    return (
      <div
        data-slot="create-workspace"
        className="designer-workspace flex min-h-0 w-full flex-1 flex-col overflow-hidden"
      >
        {renderWorkspaceAppBar(false)}
        <div className="flex min-h-0 w-full flex-1 items-center justify-center p-4 sm:p-6">
          <Card
            role="alert"
            variant="default"
            padding="none"
            className="w-full max-w-lg border-destructive/25 shadow-(--card-shadow)"
          >
            <CardContent className="space-y-5 p-6 text-center sm:p-8">
              <div>
                <h1 className="text-xl font-semibold text-foreground">
                  We couldn&apos;t open this dress
                </h1>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {sessionRestoreError}
                </p>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
                <Button asChild variant="outline" className="min-h-11">
                  <Link href="/account">Back to My Dresses</Link>
                </Button>
                {onRetrySessionRestore ? (
                  <Button
                    type="button"
                    variant="gradient"
                    className="min-h-11"
                    onClick={onRetrySessionRestore}
                  >
                    Try again
                  </Button>
                ) : (
                  <Button asChild variant="gradient" className="min-h-11">
                    <Link
                      href={
                        sessionSignInHref ??
                        sessionRecoveryHref ??
                        "/create?new=1"
                      }
                    >
                      {sessionSignInHref
                        ? "Sign in to open this dress"
                        : sessionRecoveryHref
                          ? "Continue opening this dress"
                          : "Start a new dress"}
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div
      data-slot="create-workspace"
      className="designer-workspace flex min-h-0 w-full flex-1 flex-col overflow-hidden pb-[var(--keyboard-inset,0px)] lg:pb-0"
    >
      {renderWorkspaceAppBar(showAgentChatShell)}

      <div
        data-entry={!showAgentChatShell || undefined}
        className="designer-panels flex min-h-0 min-w-0 flex-1 items-stretch justify-center overflow-hidden lg:justify-start"
      >
        {hasStartedConversation ? (
          <DesignerPanelNavigation
            view={mobileView}
            onSelect={selectMobileView}
            detailsPanelId={mobileDesignDetailsPanelId}
            detailsTriggerRef={mobileDetailsTriggerRef}
            pendingCount={pendingDetailCount}
            unreadPreviews={unreadPreviews}
            statusView={renderingSide ?? activeView}
            status={
              generationError
                ? "Preview needs another attempt"
                : quoteError
                  ? "Quote needs another attempt"
                  : tryOnStatus.status === "error"
                    ? "Try-on needs another attempt"
                    : designSubmitted
                      ? "Preparing your quote"
                      : virtualTryOnPending
                        ? "Creating your try-on"
                        : loading || isModifying
                          ? "Updating your preview"
                          : tryOnStatus.status === "complete"
                            ? "Your try-on is ready"
                            : null
            }
          />
        ) : null}
        <Card
          variant="default"
          padding="none"
          overflowHidden
          data-slot="create-agent-card"
          id="designer-chat-panel"
          role={
            !isDesktopWorkspace && hasStartedConversation
              ? "tabpanel"
              : undefined
          }
          aria-labelledby={
            !isDesktopWorkspace && hasStartedConversation
              ? "designer-mobile-tab-chat"
              : undefined
          }
          className={cn(
            "designer-well designer-chat-well min-h-0 w-full flex-1 flex-col overflow-hidden lg:flex",
            visibleMobilePanel === "chat" ? "flex" : "hidden"
          )}
        >
          <CardContent className="flex-1 min-h-0 flex flex-col overflow-hidden p-0 min-w-0">
            {showAgentChatShell ? (
              <h2 data-slot="create-chat-heading" className="sr-only">
                Dress design chat
              </h2>
            ) : null}
            {showAgentChatShell ? (
              <CreateAgentChat
                onOpenConversationRef={openConversationRef}
                onOpenHelpRef={openHelpRef}
                onMobileAttentionHost={setMobileAttentionHost}
                mobileFooterHost={mobileComposerHost}
                onAttachmentPendingChange={setComposerAttachmentPending}
                desktopFooterActionId={desktopRenderActionId}
                desktopFooterAuthorityPending={desktopFooterAuthorityPending}
                onOpenFullSizeImage={view => {
                  setInitialView(view);
                  setShowFullSizeImage(true);
                }}
                onStartOverRef={resetExampleTypingRef}
                optimisticEntryMessage={
                  pendingEntryConversation
                    ? {
                        id: `entry-${pendingEntryConversation.id}`,
                        message: pendingEntryConversation.message,
                        imageUrls: pendingEntryConversation.imageUrls,
                        createdAt: pendingEntryConversation.createdAt,
                      }
                    : undefined
                }
              />
            ) : (
              <CreateEntryPoint
                useBottomInset={false}
                onStartConversation={startEntryConversation}
                entryActionPending={Boolean(pendingEntryConversation)}
                initialPrompt={failedEntryConversation?.message ?? ""}
                initialReferences={failedReferenceImages}
                initialPersistAsDesignPrompt={
                  failedEntryConversation?.options.persistAsDesignPrompt
                }
                initialEntryPoint={failedEntryConversation?.options.entryPoint}
                initialPresetType={failedEntryConversation?.options.presetType}
                entryError={entryStartError}
              />
            )}
          </CardContent>

          {/* Full Size Image Modal */}
          <CreateFullSizeImageModal
            isOpen={showFullSizeImage}
            imageUrl={imageUrl}
            backImageUrl={backImageUrl}
            onClose={() => setShowFullSizeImage(false)}
            initialView={initialView}
            onViewChange={setCurrentView}
          />

          {/* ZipCode Modal is rendered by CreatePageModals to avoid duplicates */}

          {/* Start Over Confirmation Modal */}
          <Dialog
            open={showStartOverConfirm}
            onOpenChange={open => !open && setShowStartOverConfirm(false)}
          >
            <DialogContent
              title={null}
              onOpenAutoFocus={() => {
                startOverReturnFocusRef.current =
                  document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null;
              }}
              onCloseAutoFocus={event => {
                const trigger = startOverReturnFocusRef.current;
                if (trigger?.isConnected) {
                  event.preventDefault();
                  trigger.focus({ preventScroll: true });
                }
              }}
            >
              <DialogHeader>
                <DialogTitle>Start a new dress?</DialogTitle>
                <DialogDescription className="text-foreground/90">
                  {hasVerifiedAccount(session?.user)
                    ? "Your current dress and chat will stay safely in My Dresses. We’ll open a fresh canvas for your next idea."
                    : "Starting over clears this canvas and opens a fresh one. Create an account first if you want to keep this dress in My Dresses."}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowStartOverConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  disabled={workspaceActionDisabled}
                  onClick={() => {
                    if (
                      pendingEntryConversationRef.current ||
                      workspaceActionDisabled
                    ) {
                      return;
                    }
                    handleStartOver();
                    resetExampleTypingRef.current?.();
                    setShowStartOverConfirm(false);
                  }}
                >
                  {hasVerifiedAccount(session?.user)
                    ? "Save & start new"
                    : "Start new dress"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Card>

        <DesktopDressDesignCanvas
          mobileAttentionHost={
            hasStartedConversation ? mobileAttentionHost : null
          }
          mobileComposerBusy={
            composerAttachmentPending || Boolean(pendingEntryConversation)
          }
          onShowMobileView={selectMobileView}
          mobileVisible={visibleMobilePanel === "design"}
          tryOnStatus={tryOnStatus}
          onViewTryOn={() => setVirtualTryOnOpen(true)}
          detailsOpen={designDetailsOpen}
          detailsPanelId={
            isDesktopWorkspace
              ? designDetailsPanelId
              : mobileDesignDetailsPanelId
          }
          showInlineDetails={isDesktopWorkspace}
          detailsTriggerRef={
            isDesktopWorkspace
              ? desktopDetailsTriggerRef
              : mobileDetailsTriggerRef
          }
          onDetailsOpenChange={setDesignDetailsOpen}
          onEditInChat={intent => {
            selectedMobileViewRef.current = "chat";
            selectPanel("chat");
            openConversationRef.current?.(intent);
            window.requestAnimationFrame(() => {
              const focusReadyComposer = () => {
                if (!layoutMountedRef.current) return true;
                if (
                  !isDesktopWorkspace &&
                  selectedMobileViewRef.current !== "chat"
                )
                  return true;
                const input = document.querySelector<HTMLTextAreaElement>(
                  'textarea[aria-label="Chat message input"]'
                );
                if (!input || input.disabled) return false;
                input.focus({ preventScroll: true });
                return true;
              };
              if (focusReadyComposer()) return;

              // A just-finished render can leave the persistent composer
              // disabled for one follow-up turn. The tap explicitly requested
              // Chat, so complete that focus handoff when the input unlocks.
              const observer = new MutationObserver(() => {
                if (focusReadyComposer()) observer.disconnect();
              });
              observer.observe(document.body, {
                subtree: true,
                childList: true,
                attributes: true,
                attributeFilter: ["disabled"],
              });
              window.setTimeout(() => observer.disconnect(), 5000);
            });
          }}
          showStartOver={showAgentChatShell}
          startOverDisabled={workspaceActionDisabled}
          onStartOver={() => setShowStartOverConfirm(true)}
          tryOnDisabled={
            virtualTryOnUnavailable || virtualTryOnPending || authLoading
          }
          tryOnDisabledReason={
            restorationPending
              ? "Wait for this dress to finish loading."
              : authLoading
                ? "Checking your account."
                : loading || isModifying || virtualTryOnPending
                  ? "Wait for the current preview to finish."
                  : !imageUrl?.trim()
                    ? "Generate the front preview to unlock free try-on."
                    : !ticketId?.trim()
                      ? "Save a front preview to try on your dress."
                      : undefined
          }
          desktopRenderActionState={desktopRenderActionState}
          onTryOn={requestVirtualTryOn}
          onOpenFullscreen={handleImageClick}
        />
      </div>

      {showAgentChatShell ? (
        <div
          ref={setMobileComposerHost}
          data-slot="mobile-create-composer-host"
          className="designer-mobile-composer-host shrink-0 lg:hidden"
        />
      ) : null}

      <VirtualTryOnDialog
        key={`${panelIdentityKey}:${frontImagePath ?? imageUrl}:${backImagePath ?? backImageUrl}`}
        onWorkspaceStatusChange={setTryOnStatus}
        open={virtualTryOnOpen}
        onOpenChange={setVirtualTryOnOpen}
        frontImageUrl={imageUrl?.trim() ?? ""}
        frontImagePath={frontImagePath}
        backImageUrl={backImageUrl?.trim() || null}
        backImagePath={backImagePath}
        ticketId={ticketId}
        unavailable={virtualTryOnUnavailable}
        onPendingChange={setVirtualTryOnPending}
      />
      <LoginRequiredDialog
        open={virtualTryOnAccountGateOpen}
        onOpenChange={setShowVirtualTryOnAccountGate}
        title="Create an account to use virtual try-on"
        description="Virtual try-on is free with a confirmed email address. Create an account or sign in to continue. Your dress and chat will stay here."
      />
    </div>
  );
}
